import React, {useEffect, useState} from 'react';

import {Subtext} from 'src/design/atoms/Typography';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {useAppDispatch} from 'src/redux/hooks';
import {useGetHangfireJobDetailQuery} from 'src/redux/queries/backgroundJob';
import {getUserInstances} from 'src/redux/slices/sessionSlice';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

const POLL_INTERVAL_MS = 5000;

type CloneJobPhase = 'processing' | 'succeeded' | 'failed';

const getPhase = (state: string | undefined): CloneJobPhase => {
  switch (state) {
    case 'Succeeded':
      return 'succeeded';
    case 'Failed':
    case 'Deleted':
      return 'failed';
    default:
      return 'processing';
  }
};

export interface CloneInstanceConfirmationProps {
  id: string;
}

export const CloneInstanceConfirmation: React.FC<
  CloneInstanceConfirmationProps
> = ({id}) => {
  const {setAlert} = useAlert();
  const dispatch = useAppDispatch();

  const [phase, setPhase] = useState<CloneJobPhase>('processing');

  const {
    data: detail,
    error,
    isFetching,
  } = useGetHangfireJobDetailQuery(id, {
    // Stop polling once the job reaches a terminal state.
    pollingInterval: phase === 'processing' ? POLL_INTERVAL_MS : 0,
  });

  useEffect(() => {
    if (detail) setPhase(getPhase(detail.state));
  }, [detail]);

  useEffect(() => {
    if (error) setAlert(parseErrorMessage(error), 'error');
  }, [error, setAlert]);

  useEffect(() => {
    if (phase === 'succeeded') void dispatch(getUserInstances());
  }, [dispatch, phase]);

  return (
    <ContentWrapper>
      {phase === 'processing' && (
        <>
          <p>Your request is currently processing.</p>
          <p>
            This page will automatically update when the request completes. You
            may leave this page at any time without affecting your request.
          </p>
        </>
      )}
      {phase === 'failed' && (
        <>
          <p>Your request failed to process.</p>
          <p>
            The cloning process affects a large number of records. This failure
            may be due to many different reasons. Contact support for more
            information.
          </p>
        </>
      )}
      {phase === 'succeeded' && (
        <>
          <p>Your request is completed!</p>
          <p>
            Your new instance was successfully created, and the select items
            were cloned into it. You may now select the new instance from the
            instance dropdown.
          </p>
          <p>
            <b>Note:</b> Some secondary data may still be processed after the
            clone completes, such as uploaded files, images, floorplans, and
            calculated fields. This secondary data processing may take some
            minutes or hours to complete depending on the size of the instance
            you cloned.
          </p>
        </>
      )}
      {isFetching && <Subtext text="Checking status..." />}
    </ContentWrapper>
  );
};
